/**
 * Decide whether to prompt the user to reload for a new deploy.
 *
 * `loaded` is the deploy id captured the first time the running client saw the
 * version endpoint (i.e. the build it is actually running). `current` is the
 * latest poll. We only prompt once a baseline is captured and the live deploy
 * differs — empty/missing `current` (offline / transient) never prompts.
 */
export function isNewVersionAvailable(
  loaded: string | null,
  current: string | null | undefined,
): boolean {
  if (loaded === null) return false;
  if (typeof current !== "string" || current.length === 0) return false;
  return current !== loaded;
}

/**
 * Work a reload would destroy right now, registered by its owner (the browser
 * phone registers "a call is ringing or up").
 */
const reloadBlockers = new Set<() => boolean>();

/** Keeps a stale tab from reloading itself while `isBusy()`; returns the release. */
export function blockReloadWhile(isBusy: () => boolean): () => void {
  reloadBlockers.add(isBusy);
  return () => {
    reloadBlockers.delete(isBusy);
  };
}

/**
 * Whether a tab running a superseded deploy may reload itself now. A tab left
 * open across a deploy keeps the old bundle in memory, and once the old
 * deployment ages out (skew protection: 12 h) that bundle talks to APIs that
 * have moved on: on 2026-10-18 a two-day-old tab never registered its browser
 * phone, so an incoming call went to voicemail. The prompt alone left it so.
 * Only while nobody is looking (the tab is hidden), nothing registered is busy
 * (no call ringing or up) and the last-focused field holds no typing.
 */
export function canAutoReload(doc: {
  visibilityState: DocumentVisibilityState;
  activeElement: Element | null;
}): boolean {
  if (doc.visibilityState !== "hidden") return false;
  for (const isBusy of reloadBlockers) {
    if (isBusy()) return false;
  }
  return !holdsTyping(doc.activeElement);
}

function holdsTyping(element: Element | null): boolean {
  if (!element) return false;
  if ("value" in element && typeof element.value === "string") {
    return element.value.trim() !== "";
  }
  return (
    (element as HTMLElement).isContentEditable &&
    element.textContent.trim() !== ""
  );
}
